// RL Agent API Client
// Prefer explicit env; fallback to localhost:8001 for local dev (any port)
const inferredDevBase =
  typeof window !== 'undefined' &&
  (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1')
    ? 'http://localhost:8001'
    : '';

const API_BASE_URL = process.env.REACT_APP_API_URL || inferredDevBase || '';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const { silent401, noLogOn401, ...fetchOptions } = options;
  const url = `${API_BASE_URL}${endpoint}`;
  const token =
    typeof window !== 'undefined' ? window.localStorage.getItem('auth_token') : null;
  const accessKey =
    typeof window !== 'undefined' ? window.localStorage.getItem('access_key') : null;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(accessKey ? { 'X-Access-Key': accessKey } : {}),
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  };

  try {
    const response = await fetch(url, config);
    if (!response.ok) {
      if (response.status === 401 && silent401 !== undefined) {
        return silent401;
      }
      const err = new Error(`API call failed: ${response.status} ${response.statusText}`);
      if (response.status === 401 && noLogOn401) err._noLog = true;
      throw err;
    }
    return await response.json();
  } catch (error) {
    if (!error._noLog) console.error(`API call to ${endpoint} failed:`, error);
    throw error;
  }
};

// Individual API functions
export const startRLTraining = async (config) => {
  return apiCall('/api/rl/train', {
    method: 'POST',
    body: JSON.stringify(config),
  });
};

export const getTrainingStatus = async (sessionId) => {
  return apiCall(`/api/rl/training/${sessionId}`);
};

export const listTrainingSessions = async () => {
  return apiCall('/api/rl/training');
};

export const startEvolutionaryTraining = async (config) => {
  return apiCall('/api/rl/train/evolutionary', {
    method: 'POST',
    body: JSON.stringify(config),
  });
};

export const getEvolutionaryStatus = async (experimentId) => {
  return apiCall(`/api/rl/evolutionary/${experimentId}`);
};

export const listEvolutionaryExperiments = async () => {
  return apiCall('/api/rl/evolutionary');
};

export const deployCombinatorialBest = async (experimentId) => {
  return apiCall(`/api/rl/deploy/combinatorial-best/${experimentId}`, {
    method: 'POST',
  });
};

export const deploySelectedModels = async (experimentId, selectedCombinations) => {
  return apiCall(`/api/rl/deploy-selected-models/${experimentId}`, {
    method: 'POST',
    body: JSON.stringify(selectedCombinations),
  });
};

export const startAutonomousTrading = async (modelId) => {
  return apiCall(`/api/rl/autonomous/start/${modelId}`, {
    method: 'POST',
  });
};

export const stopAutonomousTrading = async () => {
  return apiCall('/api/rl/autonomous/stop', {
    method: 'POST',
  });
};

export const getAutonomousTradingStatus = async () => {
  return apiCall('/api/rl/autonomous/status');
};

export const listRLModels = async () => {
  return apiCall('/api/rl/models');
};

export const getRLStatus = async () => {
  return apiCall('/api/rl/status');
};

export const deployMLModel = async (modelId) => {
  return apiCall(`/api/ml/deploy/${modelId}`, {
    method: 'POST',
  });
};

export const getAvailableMLModels = async () => {
  return apiCall('/api/ml/models');
};

export const getRLPortfolio = async () => {
  return apiCall('/api/rl/portfolio', { silent401: null });
};

export const getRLTrades = async (limit) => {
  const qs = limit ? `?limit=${limit}` : '';
  return apiCall(`/api/rl/trades${qs}`);
};
export const resetRLPortfolio = async () => {
  return apiCall('/api/rl/portfolio/reset', {
    method: 'POST',
  });
};

export const getRLPerformance = async () => {
  return apiCall('/api/rl/performance');
};

export const getRLMarketData = async () => {
  return apiCall('/api/rl/market-data/current');
};

export const getAvailableSymbols = async () => {
  return apiCall('/api/rl/symbols/available');
};

export const getVisitorCount = async (increment = true) => {
  const qs = increment ? '' : '?increment=false';
  return apiCall(`/api/rl/metrics/visitors${qs}`);
};

export const getCompetitionStatus = async () => {
  return apiCall('/api/rl/competition/status');
};

// Splash/marketing email capture
export const subscribeEmail = async (email) => {
  return apiCall('/api/subscribe', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

export const startCompetitionWithBestModels = async (experimentId) => {
  return apiCall(`/api/rl/competition/start-best-models/${experimentId}`, {
    method: 'POST',
  });
};

export const startCompetition = async (config) => {
  return apiCall('/api/rl/competition/start', {
    method: 'POST',
    body: JSON.stringify(config),
  });
};

export const stopCompetition = async () => {
  return apiCall('/api/rl/competition/stop', {
    method: 'POST',
  });
};

export const resetCompetition = async () => {
  return apiCall('/api/rl/competition/reset', {
    method: 'POST',
  });
};

export const getAvailableExperiments = async () => {
  return apiCall('/api/rl/competition/available-experiments');
};

export const previewModelSelection = async (config) => {
  return apiCall('/api/rl/competition/preview-models', {
    method: 'POST',
    body: JSON.stringify(config),
  });
};

export const startEnhancedCompetition = async (config) => {
  return apiCall('/api/rl/competition/start-enhanced', {
    method: 'POST',
    body: JSON.stringify(config),
  });
};

export const listTopModelsSummary = async () => {
  return apiCall('/api/rl/models/top-summary');
};

export const getIndicatorDefaults = async () => {
  return apiCall('/api/rl/indicator-defaults');
};

export const storeSelectedModels = async (payload) => {
  return apiCall('/api/rl/models/store-selected', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const getModelTrades = async (modelId) => {
  return apiCall(`/api/rl/models/${modelId}/trades`);
};

export const deleteEvolutionaryExperiment = async (experimentId) => {
  return apiCall(`/api/rl/evolutionary/${experimentId}`, {
    method: 'DELETE',
  });
};

export const stopEvolutionaryExperiment = async (experimentId) => {
  return apiCall(`/api/rl/evolutionary/${experimentId}/stop`, {
    method: 'POST',
  });
};

// RL API Service object for RLModels component
export const rlApiService = {
  listModels: async (limit) => {
    const qs = limit ? `?limit=${limit}` : '';
    return apiCall(`/api/rl/models${qs}`);
  },

  listEvolutionaryExperiments: async () => {
    return apiCall('/api/rl/evolutionary');
  },

  deployModel: async (modelId) => {
    return apiCall(`/api/rl/deploy/${modelId}`, {
      method: 'POST',
    });
  },

  deployBestModel: async () => {
    return apiCall('/api/rl/deploy/best', {
      method: 'POST',
    });
  },

  getModel: async (modelId) => {
    return apiCall(`/api/rl/models/${modelId}`);
  },
};

// Pricing catalog APIs
export const listPricingPlans = async (couponCode) => {
  const qs = couponCode ? `?coupon_code=${encodeURIComponent(couponCode)}` : '';
  return apiCall(`/api/rl/pricing${qs}`);
};

export const calculatePricing = async ({
  symbols,
  timeframes,
  aggregation = 'max',
  couponCode,
  totalTimesteps,
  generations,
  multiPairModel = true,
  portfolios,
  competitions,
  modelsPerCompetition,
  indicatorCount,
}) => {
  return apiCall('/api/rl/pricing/calc', {
    method: 'POST',
    body: JSON.stringify({
      symbols,
      timeframes,
      aggregation,
      coupon_code: couponCode,
      total_timesteps: totalTimesteps,
      generations,
      multi_pair_model: multiPairModel,
      portfolios,
      competitions,
      models_per_competition: modelsPerCompetition,
      indicator_count: indicatorCount,
    }),
  });
};

export const getPricingPlan = async (slug, couponCode) => {
  const qs = couponCode ? `?coupon_code=${encodeURIComponent(couponCode)}` : '';
  return apiCall(`/api/rl/pricing/${slug}${qs}`);
};

export const logPricingEvent = async (payload) => {
  return apiCall('/api/rl/pricing/events', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const savePricingPlan = async (plan, adminToken) => {
  return apiCall('/api/rl/pricing/admin/plans', {
    method: 'POST',
    headers: {
      ...(adminToken ? { 'x-admin-token': adminToken } : {}),
    },
    body: JSON.stringify(plan),
  });
};

export const deletePricingPlan = async (planId, adminToken) => {
  return apiCall(`/api/rl/pricing/admin/plans/${planId}`, {
    method: 'DELETE',
    headers: {
      ...(adminToken ? { 'x-admin-token': adminToken } : {}),
    },
  });
};

export const savePricingPromo = async (promo, adminToken) => {
  return apiCall('/api/rl/pricing/admin/promos', {
    method: 'POST',
    headers: {
      ...(adminToken ? { 'x-admin-token': adminToken } : {}),
    },
    body: JSON.stringify(promo),
  });
};

export const deletePricingPromo = async (promoId, adminToken) => {
  return apiCall(`/api/rl/pricing/admin/promos/${promoId}`, {
    method: 'DELETE',
    headers: {
      ...(adminToken ? { 'x-admin-token': adminToken } : {}),
    },
  });
};

export const getPricingCatalogAdmin = async (adminToken) => {
  return apiCall('/api/rl/pricing/admin/catalog', {
    headers: {
      ...(adminToken ? { 'x-admin-token': adminToken } : {}),
    },
  });
};

export const resetPricingCatalog = async (adminToken) => {
  return apiCall('/api/rl/pricing/admin/seed', {
    method: 'POST',
    headers: {
      ...(adminToken ? { 'x-admin-token': adminToken } : {}),
    },
  });
};

// Auth APIs
export const registerUser = async ({ email, password, username }) => {
  return apiCall('/api/rl/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, username }),
  });
};

export const loginUser = async ({ email, password }) => {
  return apiCall('/api/rl/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    noLogOn401: true,
  });
};

export const getCurrentUser = async () => {
  return apiCall('/api/rl/auth/me', { silent401: { user: null } });
};

export const updateUserPreferences = async (prefs) => {
  return apiCall('/api/rl/auth/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
};

// Access key validation
export const validateAccessKey = async () => {
  return apiCall('/api/rl/keys/validate', {
    method: 'POST',
  });
};